/**
 * Shared Prev/Next pager for admin log tables (Change history, System logs).
 * Per-page sizes are fixed to 20 / 50 / 100 for a consistent UX.
 */

import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Allowed page sizes for every PaginationBar in the app. */
export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];
export const DEFAULT_PAGE_SIZE: PageSizeOption = 50;

export interface PaginationBarProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** When set, shows the per-page dropdown (20 / 50 / 100). */
  onPageSizeChange?: (pageSize: PageSizeOption) => void;
  className?: string;
}

export function pageCount(total: number, pageSize: number): number {
  if (total <= 0 || pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

export function pageRange(
  page: number,
  pageSize: number,
  total: number,
): { from: number; to: number } {
  if (total <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return { from, to };
}

export function coercePageSize(raw: number | string | null | undefined): PageSizeOption {
  const parsed = typeof raw === "number" ? raw : Number.parseInt(String(raw ?? ""), 10);
  if (PAGE_SIZE_OPTIONS.includes(parsed as PageSizeOption)) return parsed as PageSizeOption;
  return DEFAULT_PAGE_SIZE;
}

export function PaginationBar({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationBarProps) {
  const { t } = useTranslation();
  const size = coercePageSize(pageSize);
  const pages = pageCount(total, size);
  const { from, to } = pageRange(page, size, total);
  const canPrev = page > 1;
  const canNext = page < pages;

  if (total <= 0) return null;

  return (
    <div
      className={
        className ??
        "flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3 text-sm"
      }
      data-testid="pagination-bar"
    >
      <p className="text-muted-foreground" data-testid="pagination-summary">
        {t("common.paginationShowing", { from, to, total })}
        <span className="mx-2 text-border">·</span>
        {t("common.paginationPageOf", { page, pages })}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {onPageSizeChange && (
          <div className="flex items-center gap-2" data-testid="pagination-page-size">
            <label
              htmlFor="pagination-page-size"
              className="text-muted-foreground whitespace-nowrap"
            >
              {t("common.paginationPerPage")}
            </label>
            <Select
              value={String(size)}
              onValueChange={(value) => onPageSizeChange(coercePageSize(value))}
            >
              <SelectTrigger
                id="pagination-page-size"
                className="h-8 w-[4.5rem]"
                aria-label={t("common.paginationPerPage")}
                data-testid="pagination-page-size-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          aria-label={t("common.paginationPrev")}
          data-testid="pagination-prev"
        >
          <ChevronLeft className="size-4" />
          {t("common.paginationPrev")}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          aria-label={t("common.paginationNext")}
          data-testid="pagination-next"
        >
          {t("common.paginationNext")}
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}
